@if (isLoading()) {
  <div class="soft-cs-form__loading">
    <soft-ds-loading-spinner size="lg" />
    <span class="ds-body-2 text-light">Baustelle wird geladen…</span>
  </div>
} @else {
  <div class="soft-cs-form">
    <soft-ds-progress-steps
      [steps]="steps"
      [currentIndex]="stepIndex()"
      [allowJump]="true"
      data-testid="construction-site-steps"
    />

    @if (errorMessage(); as message) {
      <div class="soft-cs-form__error" role="alert" data-testid="construction-site-error">
        <soft-ds-icon name="fa-regular fa-circle-exclamation" size="sm" />
        <span class="ds-body-2">{{ message }}</span>
      </div>
    }

    <div class="soft-cs-form__steps">
      <div
        class="soft-cs-form__step"
        [class.step-hidden]="stepIndex() !== 0"
        data-testid="construction-site-step-stammdaten"
      >
        <section class="soft-cs-form__section">
          <span class="soft-cs-form__legend ds-caption-semibold">Bezeichnung</span>
          <div class="soft-cs-form__grid">
            <soft-ds-input
              label="Name 1"
              required
              placeholder="Baustelle"
              [readonly]="isReadonly()"
              [value]="value().name1"
              (valueChange)="set('name1', $event)"
              data-testid="construction-site-name1"
            />
            <soft-ds-input
              label="Kurzbezeichnung"
              [readonly]="isReadonly()"
              [value]="value().name"
              (valueChange)="set('name', $event)"
            />
            <soft-ds-input
              label="Name 2"
              [readonly]="isReadonly()"
              [value]="value().name2"
              (valueChange)="set('name2', $event)"
            />
            <soft-ds-input
              label="Name 3"
              [readonly]="isReadonly()"
              [value]="value().name3"
              (valueChange)="set('name3', $event)"
            />
          </div>
        </section>

        <section class="soft-cs-form__section">
          <div class="soft-cs-form__section-head">
            <span class="soft-cs-form__legend ds-caption-semibold">Adresse</span>
            <span class="ds-caption text-light">Suche füllt Straße, PLZ und Ort</span>
          </div>
          @if (!isReadonly()) {
            <soft-ds-search-input
              placeholder="Adresse suchen — Straße, PLZ Ort"
              [(value)]="addressSearch"
              data-testid="construction-site-address-search"
            />
            @if (addressSuggestionsLoading()) {
              <span class="ds-caption text-light">Suche Adressen…</span>
            } @else if (addressSuggestionsOpen() && addressSuggestions().length) {
              <div class="soft-cs-form__suggestions">
                @for (suggestion of addressSuggestions(); track suggestion.label) {
                  <button
                    type="button"
                    class="soft-cs-form__suggestion"
                    (click)="pickAddressSuggestion(suggestion)"
                    data-testid="construction-site-address-suggestion"
                  >
                    <soft-ds-icon name="fa-regular fa-location-dot" size="xs" />
                    <span class="truncate">{{ suggestion.label }}</span>
                  </button>
                }
              </div>
            }
          }
          <div class="soft-cs-form__grid soft-cs-form__grid--street">
            <soft-ds-input
              label="Straße"
              [readonly]="isReadonly()"
              [value]="value().street"
              (valueChange)="set('street', $event)"
              data-testid="construction-site-street"
            />
            <soft-ds-input
              label="Hausnummer"
              [readonly]="isReadonly()"
              [value]="value().streetNumber"
              (valueChange)="set('streetNumber', $event)"
            />
          </div>
          <div class="soft-cs-form__grid soft-cs-form__grid--city">
            <soft-ds-input
              label="PLZ"
              [readonly]="isReadonly()"
              [value]="value().postCode"
              (valueChange)="set('postCode', $event)"
            />
            <soft-ds-input
              label="Ort"
              [readonly]="isReadonly()"
              [value]="value().city"
              (valueChange)="set('city', $event)"
              data-testid="construction-site-city"
            />
          </div>
          <soft-ds-select
            label="Land"
            leadingIcon="fa-regular fa-globe"
            placeholder="Land suchen..."
            listboxTitle="Ländersuche"
            searchPlaceholder="Land suchen..."
            searchable
            clearable
            filterMode="external"
            emptyLabel="Kein Land gefunden"
            [items]="countryItems()"
            [value]="value().countryCode || null"
            (valueChange)="set('countryCode', $event)"
            [(search)]="countrySearch"
            [loadingMore]="countriesLoading()"
            [disabled]="isReadonly()"
            data-testid="construction-site-country"
          />
        </section>
      </div>

      <div
        class="soft-cs-form__step"
        [class.step-hidden]="stepIndex() !== 1"
        data-testid="construction-site-step-kontakt"
      >
        <section class="soft-cs-form__section">
          <span class="soft-cs-form__legend ds-caption-semibold">Kontakt</span>
          <div class="soft-cs-form__grid">
            <soft-ds-input
              label="Telefon"
              type="tel"
              [readonly]="isReadonly()"
              [value]="value().telephoneNumber"
              (valueChange)="set('telephoneNumber', $event)"
            />
            <soft-ds-input
              label="Fax"
              type="tel"
              [readonly]="isReadonly()"
              [value]="value().fax"
              (valueChange)="set('fax', $event)"
            />
            <soft-ds-input
              class="soft-cs-form__col-2"
              label="E-Mail"
              type="email"
              placeholder="name@firma.at"
              [readonly]="isReadonly()"
              [value]="value().mail1"
              (valueChange)="set('mail1', $event)"
            />
          </div>
        </section>

        <section class="soft-cs-form__section">
          <span class="soft-cs-form__legend ds-caption-semibold">Zuordnung</span>
          <div class="soft-cs-form__grid">
            <soft-ds-select
              class="soft-cs-form__col-2"
              label="Vertreter"
              leadingIcon="fa-regular fa-user-tie"
              placeholder="Vertreter suchen..."
              listboxTitle="Vertretersuche"
              searchPlaceholder="Vertreter suchen..."
              searchable
              clearable
              filterMode="external"
              emptyLabel="Kein Vertreter gefunden"
              [items]="representativeItems()"
              [value]="value().representativeId || null"
              (valueChange)="set('representativeId', $event)"
              [(search)]="representativeSearch"
              [loadingMore]="representativesLoading()"
              [disabled]="isReadonly()"
              data-testid="construction-site-representative"
            />
            <soft-ds-input
              label="Sachbearbeiter 1"
              [readonly]="isReadonly()"
              [value]="value().clerk1"
              (valueChange)="set('clerk1', $event)"
            />
            <soft-ds-input
              label="Sachbearbeiter 2"
              [readonly]="isReadonly()"
              [value]="value().clerk2"
              (valueChange)="set('clerk2', $event)"
            />
            <soft-ds-input
              label="Kostenstelle"
              [readonly]="isReadonly()"
              [value]="value().costCenter"
              (valueChange)="set('costCenter', $event)"
              data-testid="construction-site-cost-center"
            />
            <soft-ds-input
              label="UID"
              placeholder="ATU…"
              [readonly]="isReadonly()"
              [value]="value().vatId"
              (valueChange)="set('vatId', $event)"
            />
          </div>

          <soft-ds-button
            variant="ghost"
            size="sm"
            class="self-start"
            (click)="extrasOpen.set(!extrasOpen())"
            data-testid="construction-site-extras-toggle"
          >
            <soft-ds-icon
              *softSlot="'leading'"
              [name]="extrasOpen() ? 'fa-regular fa-chevron-up' : 'fa-regular fa-chevron-down'"
              size="sm"
              color="current"
            />
            Weitere Felder (Sachbearbeiter 3, Auftragsreferenz, Löschdatum)
          </soft-ds-button>

          @if (extrasOpen()) {
            <div class="soft-cs-form__grid">
              <soft-ds-input
                label="Sachbearbeiter 3"
                [readonly]="isReadonly()"
                [value]="value().clerk3"
                (valueChange)="set('clerk3', $event)"
              />
              <soft-ds-input
                label="Auftragsreferenz Bund"
                [readonly]="isReadonly()"
                [value]="value().federalOrderReference"
                (valueChange)="set('federalOrderReference', $event)"
              />
              <soft-ds-date-input
                label="Löschdatum"
                [readonly]="isReadonly()"
                [value]="value().deletionDate || null"
                (valueChange)="set('deletionDate', $event)"
              />
              <soft-ds-input
                label="Anrede"
                [readonly]="isReadonly()"
                [value]="value().salutation"
                (valueChange)="set('salutation', $event)"
              />
              <soft-ds-input
                label="Postfach"
                [readonly]="isReadonly()"
                [value]="value().poBox"
                (valueChange)="set('poBox', $event)"
              />
            </div>
          }
        </section>
      </div>

      <div
        class="soft-cs-form__step"
        [class.step-hidden]="stepIndex() !== 2"
        data-testid="construction-site-step-lieferhinweise"
      >
        <section class="soft-cs-form__section">
          <div class="soft-cs-form__section-head">
            <span class="soft-cs-form__legend ds-caption-semibold">Lieferhinweise</span>
            <span class="ds-caption text-light">Optional</span>
          </div>
          <div class="soft-cs-form__grid">
            <soft-ds-multiline-input
              class="soft-cs-form__col-2"
              label="Lieferhinweis intern"
              placeholder="Nur für Disposition und Fahrer"
              [lines]="3"
              [readonly]="isReadonly()"
              [value]="value().deliveryHint"
              (valueChange)="set('deliveryHint', $event)"
            />
            <soft-ds-multiline-input
              class="soft-cs-form__col-2"
              label="Lieferhinweis Kunde"
              placeholder="Wird auf Lieferschein gedruckt"
              [lines]="3"
              [readonly]="isReadonly()"
              [value]="value().customerDeliveryHint"
              (valueChange)="set('customerDeliveryHint', $event)"
            />
          </div>
        </section>

        <div class="soft-cs-form__summary" data-testid="construction-site-summary">
          <span class="ds-caption-semibold">Zusammenfassung</span>
          <span class="ds-caption text-light">{{ missingSummary() }}</span>
        </div>

        @if (mode() === 'update') {
          <p class="ds-caption text-light">
            Es werden nur geänderte Felder übertragen. Ein bereits gefülltes Feld kann
            über diese Schnittstelle nicht geleert werden.
          </p>
        }
      </div>
    </div>
  </div>
}

@if (!embedded()) {
  <ng-template #bottomTpl>
    <div class="soft-cs-form__actions">
      <span class="ds-caption me-auto self-center text-light">{{ stepHint() }}</span>
      <soft-ds-button
        variant="outline"
        (click)="onCancel()"
        data-testid="construction-site-cancel"
      >
        Abbrechen
      </soft-ds-button>
      @if (stepIndex() > 0) {
        <soft-ds-button
          variant="outline"
          (click)="goToStep(stepIndex() - 1)"
          data-testid="construction-site-back"
        >
          Zurück
        </soft-ds-button>
      }
      @if (stepIndex() < 2) {
        <soft-ds-button
          variant="primary"
          (click)="onPrimary()"
          data-testid="construction-site-next"
        >
          Weiter
        </soft-ds-button>
      } @else {
        <soft-ds-button
          variant="primary"
          [disabled]="!canSave()"
          [loading]="isSaving()"
          (click)="save()"
          data-testid="construction-site-save"
        >
          <soft-ds-icon *softSlot="'leading'" name="fa-regular fa-check" />
          Speichern
        </soft-ds-button>
      }
    </div>
  </ng-template>
}
